/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import type { FunctionComponent } from 'react';
import type Teacher from '@whizz/api-client/dist/@types/model/Teacher';
import type { StudentType } from '../types';
import { useI18n } from '@whizz/react-i18n';
import { useIdentity } from '@whizz/react-identity';
import { model, repository } from '@whizz/api-client';
import { GlobalDashboardLayout } from '../../layouts';
import { MessagesTable } from './modules';
import { useClassSelector } from '../../hooks/filters/useClassSelector';
import { Alert } from '../../components';
import { QUERIES_IDS } from '../../util/vars';
import { useQuery, useQueryClient } from 'react-query';
import { CongratulateButton, Headline } from '../../global';
import { useMessages } from '../../hooks/useMessages';
import NoClasses from '../../global/sections/NoClasses';

const Messages: FunctionComponent = () => {
  const { locale, translate } = useI18n();
  const { me } = useIdentity();
  const teacherId = (me as Teacher).getId();
  const queryClient = useQueryClient();

  const [ modal, setMessageStudents ] = useMessages(() => queryClient.invalidateQueries([ QUERIES_IDS.GROUP_MESSAGES ]));

  const classesQuery = useQuery(
    [ QUERIES_IDS.CLASSES, teacherId ],
    () => repository.NewTeacherRepository.getClasses(teacherId!),
    {
      enabled: null != teacherId,
      placeholderData: [],
    },
  );

  const [ classSelector, classValue ] = useClassSelector(me?.username as string, classesQuery.data!, 'space-between');

  const studentsQuery = useQuery(
    [ QUERIES_IDS.GROUP_STUDENTS, classValue?.value ],
    () => model.Group.getStudentsById(classValue!.value),
    {
      enabled: !!classValue?.value,
    },
  );

  const messageQueryEnabled = !!classValue?.value && !!studentsQuery.data?.length;
  const messagesQuery = useQuery(
    [ QUERIES_IDS.GROUP_MESSAGES, classValue?.value ],
    () => model.Message.allByGroup(classValue!.value, null, locale),
    {
      enabled: messageQueryEnabled,
    },
  );

  const getIsLoading = () => {
    if (studentsQuery.isFetched && 0 === studentsQuery.data?.length) {
      return false;
    }
    return classValue?.value && (!messagesQuery.isFetched || messagesQuery.isFetching);
  };

  const students = studentsQuery.data ?? [];
  const isLoading = getIsLoading();
  const showNoClasses = classesQuery.isFetched && 0 === classesQuery.data?.length;
  const showNoStudents = studentsQuery.isFetched && 0 === students.length;

  const newStudents: StudentType[] = students.map((student) => ({
    id: student.getId(),
    firstName: student.getName(),
    fullName: student.getName(),
  }));

  return (
    <GlobalDashboardLayout
      isLoading={isLoading}
      title={translate('pages.MESSAGES.HEADER')}
      actionContent={classSelector}
      classId={classValue?.value}
      disableContainer={!classValue}
    >
      {modal}
      <div className={'mb-lg'}>
        {!classValue ? (
          showNoClasses
            ? <NoClasses />
            : <Alert show>{translate('pages.HOME.SELECT_CLASS')}</Alert>
        ) : showNoStudents ? (
          <>
            <Headline title={translate('pages.HOME.GET_STARTED.NO_STUDENTS')} />
            <div className={'text-muted italic mt-5 space-y-2'}>
              <p>{translate('pages.HOME.GET_STARTED.NO_STUDENTS_info1')}</p>
              <p>{translate('pages.HOME.GET_STARTED.NO_STUDENTS_info2')}</p>
            </div>
          </>
        ) : (
          <>
            <div className={'flex justify-between items-center'}>
              <h2 className={'font-bold text-xl'}>{translate('pages.MESSAGES.SUB_HEADER')}</h2>
              <CongratulateButton onClick={() => setMessageStudents({ students: newStudents })} disabled={!newStudents.length} />
            </div>
            <MessagesTable data={messagesQuery.data ?? []} />
          </>
        )
        }
      </div>
    </GlobalDashboardLayout>
  );
};

export default Messages;
